import { Fragment, useMemo } from "react";

import type { KioskOpenEntry } from "../types/chipTypes";
import { CheckIcon, fmtDate, fmtRange, isoWeek, XIcon } from "../utils/openEntriesHelpers";
import { groupKioskOpenEntries } from "../utils/openEntryGroups";
import { InhaltCell } from "./InhaltCell";

export function OpenEntriesList({
  entries,
  userId,
  onOpenAttendance,
}: {
  entries: KioskOpenEntry[];
  /** Teacher user_id from the chip-login response — required so the
   *  inline diary saves stamp the right `created_by` on the paired
   *  kiosk. Plumbed through to InhaltCell. */
  userId: string;
  onOpenAttendance: (entry: KioskOpenEntry) => void;
}) {
  // Fold consecutive same-class/subject lessons into Doppelstunde groups
  // so the teacher fills one row for the whole block. A 1-lesson group
  // renders exactly like the previous flat row.
  const groups = useMemo(() => groupKioskOpenEntries(entries), [entries]);

  if (entries.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3">
        <div className="px-6 py-10 text-center text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl">
          Keine offenen Einträge — alles erfasst.
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto rounded-xl border border-amber-200 overflow-hidden bg-white">
      <div className="px-4 py-2.5 bg-amber-100/60 border-b border-amber-200 text-sm font-medium text-amber-900">
        Offene Einträge ({entries.length})
      </div>
      <table className="w-full text-sm">
        <thead className="bg-amber-50 text-xs text-slate-700">
          <tr className="text-left">
            <th className="px-4 py-2">Datum</th>
            <th className="px-4 py-2">Stunde</th>
            <th className="px-4 py-2">Fach</th>
            <th className="px-4 py-2">Klasse</th>
            <th className="px-4 py-2 text-center">Unterrichtsinhalt</th>
            <th className="px-4 py-2 text-center">Anwesenheit</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group, idx) => {
            // The first entry inside a group acts as the row's anchor —
            // earliest period, oldest in time. The last entry contributes
            // its end_at so the displayed range spans the Doppelstunde.
            const first = group.entries[0];
            const last = group.entries[group.entries.length - 1];
            const isDoppelstunde = group.entries.length > 1;

            // Week heading uses the group anchor. Compare against the
            // PREVIOUS GROUP's anchor (not the previous individual entry)
            // so the week count reflects groups, not raw lessons.
            const week = isoWeek(first.start_at);
            const prevWeek =
              idx > 0 ? isoWeek(groups[idx - 1].entries[0].start_at) : null;
            const isFirstOfWeek = week !== prevWeek;
            const weekCount = groups.filter(
              (g) => isoWeek(g.entries[0].start_at) === week,
            ).length;

            const allHaveContent = group.entries.every((e) => e.has_content);
            const allHaveAttendance = group.entries.every(
              (e) => e.has_attendance,
            );
            const lessonIds = group.entries.map((e) => e.lesson_id);

            // Period column: range when grouped (e.g. "3.–4. St"), single
            // when not. We piggy-back fmtRange for the time string so a
            // 3.+4. Stunde block reads "3.–4. St · 09:50–11:20".
            const periodLabel = isDoppelstunde
              ? `${first.period_number}.–${last.period_number}. St`
              : first.period_label ?? `${first.period_number}. St`;
            const timeRange = fmtRange(first.start_at, last.end_at);

            return (
              <Fragment key={group.key}>
                {isFirstOfWeek && (
                  <tr className="bg-amber-200/40 border-t border-amber-300">
                    <td
                      colSpan={6}
                      className="px-4 py-1.5 text-xs font-semibold text-amber-900 uppercase tracking-wide"
                    >
                      KW {week} · {weekCount}{" "}
                      {weekCount === 1 ? "fehlender Eintrag" : "fehlende Einträge"}
                    </td>
                  </tr>
                )}
                <tr className="border-t border-amber-200/50 text-slate-700">
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {fmtDate(first.start_at)}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-xs">
                    {isDoppelstunde ? (
                      <span className="inline-flex flex-col">
                        <span className="font-medium text-slate-700">
                          {periodLabel}
                        </span>
                        <span className="text-slate-500">{timeRange}</span>
                      </span>
                    ) : (
                      timeRange
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className="inline-block px-1.5 py-0.5 rounded text-xs font-medium text-white"
                      style={{ backgroundColor: first.subject_color ?? "#64748b" }}
                      title={first.subject_name ?? undefined}
                    >
                      {first.subject_abbreviation ?? first.subject_name ?? "—"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {first.class_name ?? "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    <InhaltCell
                      lessonId={first.lesson_id}
                      lessonIds={isDoppelstunde ? lessonIds : undefined}
                      userId={userId}
                      hasContent={allHaveContent}
                      onOpenKlassenbuch={() => onOpenAttendance(first)}
                    />
                  </td>
                  <td className="px-4 py-2.5">
                    {allHaveAttendance ? (
                      <div className="flex justify-center">
                        <CheckIcon />
                      </div>
                    ) : (
                      <button
                        onClick={() => onOpenAttendance(first)}
                        className="flex justify-center w-full"
                        title="Im Klassenbuch öffnen"
                      >
                        <XIcon />
                      </button>
                    )}
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
